/**
 * Paraşüt API V4 Entegrasyon Yardımcısı
 */

const PARASUT_CONFIG = {
  clientId: process.env.REACT_APP_PARASUT_CLIENT_ID,
  clientSecret: process.env.REACT_APP_PARASUT_CLIENT_SECRET,
  username: process.env.REACT_APP_PARASUT_USERNAME,
  password: process.env.REACT_APP_PARASUT_PASSWORD,
  companyId: process.env.REACT_APP_PARASUT_COMPANY_ID,
};

const PARASUT_API_BASE = "/api/parasut/v4";
const TOKEN_URL = "/api/parasut/oauth/token";

let _cachedToken = null;
let _tokenExpiresAt = 0;

export const getParasutToken = async () => {
  const now = Date.now() / 1000;
  if (_cachedToken && now < _tokenExpiresAt - 60) {
    return _cachedToken;
  }

  const response = await fetch(TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      grant_type: "password",
      client_id: PARASUT_CONFIG.clientId,
      client_secret: PARASUT_CONFIG.clientSecret,
      username: PARASUT_CONFIG.username,
      password: PARASUT_CONFIG.password,
      redirect_uri: "urn:ietf:wg:oauth:2.0:oob",
    }),
  });

  if (!response.ok) throw new Error("Paraşüt token alınamadı.");

  const data = await response.json();
  _cachedToken = data.access_token;
  _tokenExpiresAt = now + (data.expires_in || 7200);
  return _cachedToken;
};

const parasutFetch = async (path, options = {}) => {
  const token = await getParasutToken();
  const url = `${PARASUT_API_BASE}/${PARASUT_CONFIG.companyId}${path}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.errors?.[0]?.detail || `HTTP ${response.status}`);
  }
  return data;
};

// Ürün/hizmet kartı bul veya oluştur
export const findOrCreateProduct = async (name, vatRate) => {
  const productName = name ? name.trim() : "Hizmet Bedeli";
  
  const search = await parasutFetch(`/products?filter[name]=${encodeURIComponent(productName)}`);
  if (search?.data?.length > 0) {
    return search.data[0].id;
  }

  const payload = {
    data: {
      type: "products",
      attributes: {
        name: productName,
        vat_rate: vatRate !== null && vatRate !== undefined ? parseFloat(vatRate) : 20,
        unit: "Adet",
        list_price: 0,
        currency: "TRL",
        inventory_tracking: false
      }
    }
  };

  const created = await parasutFetch("/products", {
    method: "POST",
    body: JSON.stringify(payload)
  });

  return created.data.id;
};

export const findOrCreateContact = async ({ name, taxNumber, taxOffice, email, address }) => {
  if (taxNumber) {
    const search = await parasutFetch(`/contacts?filter[tax_number]=${encodeURIComponent(taxNumber)}`);
    if (search?.data?.length > 0) return search.data[0].id;
  }

  const isCompany = taxNumber && taxNumber.length === 10;
  const payload = {
    data: {
      type: "contacts",
      attributes: {
        name: name || "İsimsiz Müşteri",
        contact_type: isCompany ? "company" : "person",
        tax_number: taxNumber || "",
        tax_office: taxOffice || "",
        email: email || "",
        address: address || "",
        account_type: "customer",
      },
    },
  };

  const created = await parasutFetch("/contacts", { method: "POST", body: JSON.stringify(payload) });
  return created.data.id;
};

export const createSalesInvoice = async (invoice, contactId) => {
  const issueDate = invoice.Created_DateTime
    ? new Date(invoice.Created_DateTime).toISOString().split("T")[0]
    : new Date().toISOString().split("T")[0];

  const invoiceNumeric = parseInt(String(invoice.Invoince_Number || "").replace(/\D/g, ""), 10) || invoice.ID;

  let invoiceLinesData = [];
  const rawLines = invoice.Invoince_Lines || [];

  if (rawLines.length > 0) {
    for (const line of rawLines) {
      const pName = (line.ProductName || "Araç Taşıma Hizmet Bedeli").trim();
      
      // KDV Hatası Çözümü (%0 kabul edilir)
      const pVat = line.VatRate !== null && line.VatRate !== undefined ? parseFloat(line.VatRate) : 20;
      
      const productId = await findOrCreateProduct(pName, pVat);

      invoiceLinesData.push({
        type: "sales_invoice_details",
        attributes: {
          quantity: Math.max(parseFloat(line.Quantity) || 1, 1),
          unit_price: Math.max(parseFloat(line.UnitPrice) || 0, 0.01),
          vat_rate: pVat,
          description: pName
        },
        relationships: {
          product: {
            data: { type: "products", id: String(productId) }
          }
        }
      });
    }
  } else {
    // Fallback: Kalem yoksa
    const amountHT = parseFloat(invoice.Amount_HT) || 1.0;
    const pName = "Araç Taşıma Operasyon Hizmeti";
    const productId = await findOrCreateProduct(pName, 20);

    invoiceLinesData.push({
      type: "sales_invoice_details",
      attributes: {
        quantity: 1,
        unit_price: amountHT,
        vat_rate: 20,
        description: pName
      },
      relationships: {
        product: {
          data: { type: "products", id: String(productId) }
        }
      }
    });
  }

  const payload = {
    data: {
      type: "sales_invoices",
      attributes: {
        item_type: "invoice",
        description: `LojGo - Fatura - ${invoice.Invoince_Number || invoice.ID}`,
        issue_date: issueDate,
        due_date: issueDate,
        invoice_series: "LJG",
        invoice_id: String(invoiceNumeric),
        currency: "TRL",
        shipment_included: false
      },
      relationships: {
        contact: {
          data: { type: "contacts", id: String(contactId) },
        },
        details: {
          data: invoiceLinesData
        }
      },
    },
  };

  const result = await parasutFetch("/sales_invoices", {
    method: "POST",
    body: JSON.stringify(payload),
  });

  return { parasutInvoiceId: result.data.id };
};

export const syncInvoiceToParasut = async (invoice) => {
  const customer = invoice.Users || {};
  const invoiceInfo = invoice.Users_Invoince || {};
  const company = invoiceInfo.Users_Company || {}; 

  let contactId = invoiceInfo.Parasut_Contact_ID || null;

  const customerName =
    company.Users_Company_Name || 
    customer.Users_NameSurname ||
    customer.NameSurname ||
    invoiceInfo.KdvNumber || 
    "İsimsiz Müşteri";

  if (!contactId) {
    contactId = await findOrCreateContact({
      name: customerName,
      taxNumber: invoiceInfo.KdvNumber || customer.Users_Tc || customer.Tc,
      taxOffice: invoiceInfo.VergiDairesi,
      email: customer.Users_MailAddress || customer.Email || customer.email,
      address: invoiceInfo.Adres,
    });

    if (invoiceInfo.ID) {
      await fetch(`/api/hiflow/Users_Invoince(${invoiceInfo.ID})`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Parasut_Contact_ID: contactId }),
      });
    }
  }

  const { parasutInvoiceId } = await createSalesInvoice(invoice, contactId);

  await fetch(`/api/hiflow/Invoince(${invoice.ID})`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ Parasut_Invoice_ID: parasutInvoiceId }),
  });

  return parasutInvoiceId;
};